import { AuthorComposer, ContributorRole } from '@solo/app-core';
import { useState, useEffect } from 'react';
import { translate } from '@/app/i18n';
import { Button, Flex, Input, Text } from '@/app/ui/atoms';
import { Icon, InputTextAutocomplete, NumericInputWithLabel } from '@/app/ui/molecules';
import { stringToInt } from '@/app/utils/StringToInt';

interface AuthorsComposersTableComponentPorps {
  recordingAuthors: Array<AuthorComposer>;
  deleteRecordingAuthor: (authorComposer: AuthorComposer) => void;
  saveRecordingAuthor: (recordingAuthor: AuthorComposer) => void;
  roles: ContributorRole[];
}

const AuthorsComposersTableComponent = ({
  recordingAuthors,
  deleteRecordingAuthor,
  saveRecordingAuthor,
  roles,
}: AuthorsComposersTableComponentPorps) => {
  const [rolesItems, setRolesItems] = useState<Array<{ value: string; label: string }>>([]);

  useEffect(() => {
    setRolesItems(
      roles.map((role: ContributorRole) => ({
        value: role.id.toString(),
        label: role.name,
      })),
    );
  }, [roles]);

  return (
    <Flex.Container className="table" flexDirection="column">
      <Flex.Container>
        <Flex.Container flex={1} gap="1rem">
          <Flex.Item flex={8} className="th">
            <Text.LabelSmall>{translate('WORKS_PAGES.AUTHOR_COMPOSERS')}</Text.LabelSmall>
          </Flex.Item>
          <Flex.Item flex={6} className="th">
            <Text.LabelSmall>{translate('WORKS_PAGES.PUBLISHING_TYPE')}</Text.LabelSmall>
          </Flex.Item>
          <Flex.Item flex={6} className="th">
            <Text.LabelSmall>{translate('TRACKS.ROLE')}</Text.LabelSmall>
          </Flex.Item>
          <Flex.Item flex={2} className="th">
            <Text.LabelSmall>{translate('TRACKS.SHARES')}</Text.LabelSmall>
          </Flex.Item>
          <Flex.Item flex={1} className="th" />
        </Flex.Container>
      </Flex.Container>
      <Flex.Container flexDirection="column" gap="10px">
        {recordingAuthors.map((recordingAuthor) => (
          <Flex.Container
            key={`${recordingAuthor.authorComposerId}_${recordingAuthor.authorComposerName}`}
            className="tr"
            alignItems="center"
          >
            <Flex.Container flex={1} gap="1rem">
              <Flex.Container className="td" flex={8} alignItems="center">
                <Input.Text
                  value={`${recordingAuthor.authorComposerName} ${
                    recordingAuthor.ipi ? `(${recordingAuthor.ipi})` : ''
                  }`}
                  disabled
                />
              </Flex.Container>
              <Flex.Container className="td" flex={6} alignItems="center">
                <Input.Text
                  value={`${
                    recordingAuthor.rightsId
                      ? translate(`AUTHOR_COMPOSER_RIGHTS.INFO.${recordingAuthor.rightsId}`)
                      : '-'
                  } ${recordingAuthor.publisherName ? `(${recordingAuthor.publisherName})` : ''}`}
                  disabled
                />
              </Flex.Container>
              <Flex.Container flex={6}>
                <InputTextAutocomplete
                  placeholder={translate('WORKS_PAGES.TYPES_OF_ROLE')}
                  items={rolesItems}
                  onChange={(role) =>
                    saveRecordingAuthor({
                      ...recordingAuthor,
                      roleId: stringToInt(role),
                    })
                  }
                  defaultOption={rolesItems.find(
                    (i) => i.value === recordingAuthor.roleId.toString(),
                  )}
                />
              </Flex.Container>
              <Flex.Container flex={2}>
                <NumericInputWithLabel
                  value={recordingAuthor.mechanicalSplit}
                  placeholder="100%"
                  onChange={(mechanicalSplit) =>
                    saveRecordingAuthor({
                      ...recordingAuthor,
                      mechanicalSplit,
                    })
                  }
                />
              </Flex.Container>
              <Flex.Item flex={1} className="td" style={{ marginTop: '1rem' }}>
                <Button
                  onClick={() => deleteRecordingAuthor(recordingAuthor)}
                  style={{ marginLeft: '10px' }}
                  $transparent
                >
                  <Icon.BinIcon width={15} />
                </Button>
              </Flex.Item>
            </Flex.Container>
          </Flex.Container>
        ))}
      </Flex.Container>
      <Flex.Container className="tr" alignItems="center" style={{ marginTop: '20px' }}>
        <Flex.Container flex={1} justifyContent="flex-end">
          <Flex.Item flex={19} className="td" />
          <Flex.Item flex={3} className="td">
            <Text.SectionSubtitleBold>Total</Text.SectionSubtitleBold>
          </Flex.Item>
          <Flex.Item flex={2} className="td">
            <Text.SectionSubtitleBold>
              {recordingAuthors
                .map((recordingAuthor) => recordingAuthor.mechanicalSplit)
                .reduce((sum, element) => (sum || 0) + (element || 0), 0)}
              %
            </Text.SectionSubtitleBold>
          </Flex.Item>
          <Flex.Item flex={1} className="td" />
        </Flex.Container>
      </Flex.Container>
    </Flex.Container>
  );
};

export default AuthorsComposersTableComponent;
